/**
 * §1 — the numbered section, and the header that opens it (client, 2026-09-16: *"section it
 * systematically … everything clear and beautifully done with modern touch"*).
 *
 * The page is five sections now rather than one scroll of cards, and this file is the
 * grammar every one of them shares: a large roman numeral in a brand-tinted disc, the
 * section's title, one line saying what the reader is looking at, and a hairline that fades
 * out to the right so the rule reads as an opening flourish rather than as a box edge.
 *
 * Two things are deliberate:
 *
 * * **The section owns the anchor, not the header.** `id="sec-<key>"` sits on the
 *   `<section>` element, so `SectionNav`'s pill scrolls to the top of the *content* and the
 *   scroll-spy observes the whole section rather than a 40-pixel strip of heading.
 * * **The entrance is per section and happens once.** An `IntersectionObserver` flips the
 *   section to `.in` the first time any part of it reaches the viewport and then
 *   disconnects: nothing re-animates on the way back up, and a browser (or a jsdom test)
 *   with no observer renders every section visible from the first paint rather than blank.
 *   The animation itself is CSS and is off under `prefers-reduced-motion`.
 */
import { useEffect, useRef, useState } from 'react';

import type { ReactNode } from 'react';

import type { DashboardSectionKey } from './dashboard';

export interface SectionHeaderProps {
  /** `I`…`V` — printed in the disc, hidden from the accessibility tree (the title says it). */
  numeral: string;
  title: string;
  /** Omitted on a section whose title already says what it is (I, III). */
  description?: string;
  /** The `<h3>`'s id, so the `<section>` can be labelled by it. */
  titleId: string;
}

export function SectionHeader({ numeral, title, description, titleId }: SectionHeaderProps) {
  return (
    <div className="dash-sec-head">
      <span className="dash-sec-num" aria-hidden="true">
        {numeral}
      </span>
      <div className="dash-sec-text">
        <h3 className="dash-sec-title" id={titleId}>
          {title}
        </h3>
        {description ? <p className="dash-sec-desc">{description}</p> : null}
      </div>
      <span className="dash-sec-rule" aria-hidden="true" />
    </div>
  );
}

export interface DashSectionProps extends Omit<SectionHeaderProps, 'titleId'> {
  sectionKey: DashboardSectionKey;
  children: ReactNode;
}

export function DashSection({
  sectionKey,
  numeral,
  title,
  description,
  children,
}: DashSectionProps) {
  const ref = useRef<HTMLElement | null>(null);
  /*
    Starts `false` only where there is an observer to flip it: with no `IntersectionObserver`
    (jsdom, and any browser old enough to lack it) the section must be *visible*, not
    permanently faded out — an entrance effect is never allowed to be the reason a figure is
    missing from the page.
  */
  const [seen, setSeen] = useState(typeof IntersectionObserver !== 'function');

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver !== 'function') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const titleId = `sec-${sectionKey}-title`;
  return (
    <section
      ref={ref}
      id={`sec-${sectionKey}`}
      data-testid={`dash-section-${sectionKey}`}
      className={`dash-sec${seen ? ' in' : ''}`}
      aria-labelledby={titleId}
    >
      <SectionHeader
        numeral={numeral}
        title={title}
        description={description}
        titleId={titleId}
      />
      <div className="dash-sec-body">{children}</div>
    </section>
  );
}
